import React, { useEffect, useState } from "react";
import { useDrawerContext } from "../../../context/drawerContext";
import { useNavigate } from "react-router";
import ShopParticipantCard from "../../../components/Shop/ShopParticipantCard";
import { useUserContext } from "../../../context/userContext";
import { useHeaderContext } from "../../../context/headerContext";
import { fetchWithRefresh } from "../../../utils/fetchWithRefresh";
import { useCartContext } from "../../../context/cartContext";
import Button from "../../../components/Button";
import CloseIcon from "/public/images/icons/close.svg?react";
import SelectInput from "../../../components/SelectInput";
import QuantityInput from "../../../components/QuantityInput";
import generateUUID from "../../../utils/generateUUID";
import UpgradeAvailable from "../../../components/UpgradeAvailable";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { LogErrors } from "../../../utils/LogErrors";

export default function MyParticipants() {
  const { user, logout, updateTokens, participants, setParticipants } = useUserContext();
  const { setTitle, setBackButton, setCartButton, setCartButtonFunc } = useHeaderContext();
  const { addParticipant, addProduct, setSelectedParticipant, selectedParticipant, participants: cartParticipants, cart } = useCartContext();
  const navigate = useNavigate();
  const { setOpen, setDrawerContent } = useDrawerContext();
  const [bandTypes, setBandTypes] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [apiCartData, setApiCartData] = useState([]);
  const [cartItems, setCartItems] = useState(null);

  useEffect(() => {
    fetchCart();
  }, []);
  
  const fetchCart = async () => {
    try {
      return await Sentry.startSpan({ name: "Fetch Cart", op: "http.request" }, async (span) => {
        const url = `${import.meta.env.VITE_API_URL}Cart/Cart?userAccountId=${user.id}`;
        span.setAttribute("http.url", url);
        span.setAttribute("userId", user?.id);
  
        const response = await fetchWithRefresh(
          url,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user.token}`,
            },
          },
          logout,
          updateTokens,
          (message) => { toast.info(message); }
        );
  
        if (!response.ok) {
          throw new Error(`Failed to fetch cart. Status: ${response.status}`);
        }
  
        const data = await response.json();
        setCartItems(data.cartItems);
        span.setAttribute("cartItems.count", data?.cartItems?.length || 0);
  
        const groupedCart = data.cartItems.reduce((acc, item) => {
          if (!acc[item.ParticipantId]) {
            acc[item.ParticipantId] = [];
          }
          acc[item.ParticipantId].push({
            CartLineItemId: item.CartLineItemId,
            Quantity: item.Quantity,
          });
          return acc;
        }, {});
  
        const totalQuantities = Object.entries(groupedCart).map(([participantId, items]) => ({
          participantId,
          totalQuantity: items.reduce((sum, item) => sum + item.Quantity, 0),
        }));
  
        setApiCartData(totalQuantities);
        span.setAttribute("cart.participantCount", totalQuantities.length);
      });
    } catch (error) {
      Sentry.captureException(error, { extra: { userId: user?.id } });
      LogErrors(user, logout, updateTokens, "Error fetching cart: " + error);
    }
  };

  useEffect(() => {
    setTitle("My Participants");
    setCartButton(true);
    setBackButton(true);

    const handleCartButton = () => {
      navigate('/cart');
    };

    setCartButtonFunc(handleCartButton);
  }, []);

  useEffect(() => {
    if (user) {
      getParticipants();
      fetchAvailableBandTypes();
      fetchCurrentSubscription();
    }
  }, [user]);

  const getParticipants = async () => {
    try {
      return await Sentry.startSpan({ name: "Fetch Participants", op: "http.request" }, async (span) => {
        const url = `${import.meta.env.VITE_API_URL}Cart/FetchCartUserParticipants?UserLoginId=${user.id}`;
        span.setAttribute("http.url", url);
        span.setAttribute("userId", user?.id);
  
        const response = await fetchWithRefresh(
          url,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user.token}`,
            },
          },
          logout,
          updateTokens,
          (message) => { toast.info(message); }
        );
  
        if (!response.ok) {
          throw new Error(`Failed to fetch participants. Status: ${response.status}`);
        }
  
        const data = await response.json();
        setParticipants(data);
        span.setAttribute("participants.count", data?.length || 0);
      });
    } catch (error) {
      Sentry.captureException(error, { extra: { userId: user?.id } });
      LogErrors(user, logout, updateTokens, "Error fetching participants: " + error);
    }
  };

  const fetchCurrentSubscription = async () => {
    try {
      return await Sentry.startSpan({ name: "Fetch Subscription", op: "http.request" }, async (span) => {
        const url = `${import.meta.env.VITE_API_URL}Cart/FetchCurrentSubscription?UserLoginId=${user.id}`;
        span.setAttribute("http.url", url);
        span.setAttribute("userId", user?.id);
  
        const response = await fetchWithRefresh(
          url,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user.token}`,
            },
          },
          logout,
          updateTokens,
          (message) => { toast.info(message); }
        );
  
        if (!response.ok) {
          throw new Error(`Failed to fetch subscription. Status: ${response.status}`);
        }
  
        const data = await response.json();
        setCurrentSubscription(data);
        span.setAttribute("subscription.active", !!data?.Active);
      });
    } catch (error) {
      Sentry.captureException(error, { extra: { userId: user?.id } });
      LogErrors(user, logout, updateTokens, "Error fetching subscription: " + error);
    }
  };
  
  const fetchAvailableBandTypes = async () => {
    try {
      return await Sentry.startSpan({ name: "Fetch Band Types", op: "http.request" }, async (span) => {
        const url = `${import.meta.env.VITE_API_URL}Cart/FetchBandAvailableTypes`;
        span.setAttribute("http.url", url);
  
        const response = await fetchWithRefresh(
          url,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user.token}`,
            },
          },
          logout,
          updateTokens,
          (message) => { toast.info(message); }
        );
  
        if (!response.ok) {
          throw new Error(`Failed to fetch band types. Status: ${response.status}`);
        }
  
        const data = await response.json();
        setBandTypes(data);
        span.setAttribute("bandTypes.count", data?.length || 0);
      });
    } catch (error) {
      Sentry.captureException(error);
      LogErrors(user, logout, updateTokens, "Error fetching band types: " + error);
    }
  };  

  const ParticipantBandSelectionDrawer = (participant) => {
    setSelectedParticipant(participant.ParticipantId);
    addParticipant(participant);
    setOpen(true);
    setDrawerContent(<BandSelectionDrawer addProduct={addProduct} setOpen={setOpen} Bands={bandTypes} setDrawerContent={setDrawerContent} />);
  };

  return (
    <div className="bg-sportsLightGrey p-[20px] min-h-screen pb-[100px]">
      <h1 className="text-[24px] leading-[34px] mb-[24px]">Choose participant that requires a new band</h1>
      {participants?.length > 0 && (
        <>
          <div className={`flex gap-y-[34px] flex-col ${selectedParticipant ? 'mb-[34px]' : 'mb-4'}`}>
            {participants.slice().reverse().map((participant) => {
              // Find the total quantity for the current participant
              const participantCartData = apiCartData.find(item => item.participantId === participant.ParticipantId);
              const totalQuantity = participantCartData ? participantCartData.totalQuantity : 0;

              return (
                <ShopParticipantCard 
                  key={participant.ParticipantId}
                  participant={participant} 
                  onClick={() => ParticipantBandSelectionDrawer(participant)}
                  totalQuantity={totalQuantity}
                />
              );
            })}
          </div>
        </>
      )}
      <div className="flex gap-y-[34px] flex-col mb-4">
        {Array.isArray(cart.cartItems) && cart.cartItems.filter(cartItem => cartItem.ParticipantPending === 1)
          .map((participant) => {
            return (
              <ShopParticipantCard 
                key={participant.ParticipantId} 
                participant={participant} 
                isInParticipants={false}
              />
            );
          })
        }
      </div>
      <div className="border-t border-dashed my-[20px] pt-[20px] flex flex-col gap-[12px]">
        {currentSubscription && (
          <>
            <p className="text-sportsDarkGrey font-bold">{user?.subscription?.Name} Plan:</p>
            {currentSubscription?.RemainingParticipants <= 0 ? (
              <>
                <p className="text-sportsDarkGrey">You have 0 registrations remaining:</p>
                <p className="text-sportsDarkGrey">Upgrade your subscription plan to add a new participant</p>
                <UpgradeAvailable />
              </>
            ) : (currentSubscription?.RemainingParticipants >= 1 ? (
              <>
                <p className="text-sportsDarkGrey">You have {currentSubscription?.RemainingParticipants} {currentSubscription?.RemainingParticipants > 1 ? 'Registrations' : 'Registration'} remaining</p>
                <div className="w-full">
                  <Button variant="quaternary" className="w-full" href="/shop/my-participants/add-new-participant">+ Add Another Participant</Button>
                </div>
              </>
            ) : null)}
          </>
        )}
      </div>
    </div>
  );
}

const BandSelectionDrawer = ({ addProduct, setOpen, Bands, setDrawerContent }) => {
  const [BandType, setBandType] = useState("");
  const [BandColour, setBandColour] = useState("");
  const [Quantity, setQuantity] = useState(1);
  const [bandColours, setBandColours] = useState([]);
  const [BandPrice, setBandPrice] = useState(null);

  const { selectedParticipant } = useCartContext();
  const navigate = useNavigate();

  const getBandColours = (bandType, bands) => {
    const band = bands.find(b => b.OrgPartnerFriendlyName === bandType);
    if (band && band.BandColours) {
      return band.BandColours.split('|').map(colour => colour.trim());
    }
    return [];
  };

  useEffect(() => {
    const colours = getBandColours(BandType, Bands);
    setBandColours(colours);

    if (BandType) {
      const selectedBand = Bands.find(band => band.OrgPartnerFriendlyName === BandType);
      if (selectedBand) {
        setBandPrice(selectedBand.OrgPrice);
      }
    }

    if (colours.length === 1) {
      setBandColour(colours[0]);
    }
  }, [BandType, Bands]);

  const createProduct = () => {
    const selectedBand = Bands.find(band => band.OrgPartnerFriendlyName === BandType);

    return {
      ProductId: generateUUID(),
      BandType,
      BandColour,
      Quantity,
      BandImage: selectedBand ? selectedBand.BandImage || selectedBand.Org3dImageURL : '',
      ParticipantId: selectedParticipant,
      Price: selectedBand ? BandPrice : '',
      Org3dImageURL: selectedBand ? selectedBand.Org3dImageURL : '',
      OrgID: selectedBand ? selectedBand.OrgID : '',
      OrgPartnerFriendlyName: selectedBand ? selectedBand.OrgPartnerFriendlyName : '',
      OrgSiteLogo: selectedBand ? selectedBand.OrgSiteLogo : '',
      pricemax: selectedBand ? selectedBand.pricemax : '',
      prmedicalmax: selectedBand ? selectedBand.prmedicalmax : '',
      prnamemax: selectedBand ? selectedBand.prnamemax : ''
    };
  };

  return (
    <div className="w-full">
      <div className="flex items-center justify-between w-full">
        <br />
        <h2 className="leading-5 tracking-widest uppercase text-[12px]">
          CHOOSE BAND
        </h2>
        <CloseIcon className='cursor-pointer' onClick={() => {setOpen(false);setDrawerContent(<div></div>);}} />
      </div>
      <div className="mt-6 flex flex-col gap-2 justify-stretch">
        <h3 className="text-[24px]">Please choose the band type you wish to order</h3>
        <p className="text-sportsDarkGrey mb-[26px]">
          This can be a Sports Guardian branded (with a choice of 5 colours) or a Venue specific branded band.
        </p>
        <div className="flex flex-col gap-4">
          <SelectInput 
            label="Band Type"
            placeholder="Please Select"
            name="BandType"
            className="capitalize"
            options={Bands?.map((band, index) => (
              <option key={index} value={band.OrgPartnerFriendlyName} className="capitalize">
                {band.OrgPartnerFriendlyName}
              </option>
            ))}
            onChange={(e) => setBandType(e.target.value)}
          />
          <div className={`${BandType ? 'block' : 'hidden'}`}>
            <SelectInput 
              label="Band Colour"
              placeholder="Please Select"
              name="BandColour"
              className="capitalize"
              options={bandColours.map((colour, index) => (
                <option key={index} value={colour} className="capitalize">{colour}</option>
              ))}
              onChange={(e) => setBandColour(e.target.value)}
              value={BandColour}
            />
          </div>
          <div className={`${BandType && BandColour ? 'block' : 'hidden'}`}>
            <QuantityInput 
              min={1} 
              label={`Quantity (£${BandPrice} Each)`}
              onChange={(value) => setQuantity(value)}
            />
          </div>
        </div>
        <div className={`${BandType && BandColour && Quantity ? 'block' : 'hidden'}`}>
          <Button 
            variant="primary" 
            className="w-full mt-4"
            onClick={() => {
              const product = createProduct();
              addProduct(product);
              setBandType("");
              setBandColour("");
              setQuantity(1);
              setBandColours([]);
              setBandPrice(null);
              setOpen(false);
              setDrawerContent(<div></div>);
              navigate(`/shop/product-summary?productId=${product.ProductId}&participantId=${selectedParticipant}`, { state: {edit: false}});
            }}
          >
            Confirm & Continue
          </Button>
        </div>
      </div>
    </div>
  );
};

  // MyParticipants.propTypes = {
    
  // }
